import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import { TimeOffBlockType } from '../types';
import {
  DayPortionDTO,
  PlannerTimeoffDayResponse,
} from '../types/dto/response/planner-timeoff-response';
import { getDayPartSlot, getPortionFraction, HEADER_OFFSET_PX } from './time-off-layout';

/**
 * Default daily capacity in minutes when a member's capacity is unknown for a
 * given date (8h). HOURLY portions are sized as a fraction of this capacity.
 */
export const DEFAULT_CAPACITY_MINUTES = 480;

/**
 * Sums a time-off's days into a fractional number of full working days.
 *
 * Each non-HOURLY day contributes its portion fraction (FULL = 1, HALF = 0.5,
 * QUARTER = 0.25). HOURLY days contribute `minutes / fullWorkingDayMinutes`,
 * where the full working day is inferred from the first non-HOURLY day in the
 * block (e.g. a QUARTER day of 120min implies an 8h day) and falls back to
 * {@link DEFAULT_CAPACITY_MINUTES} when the block is entirely HOURLY.
 *
 * This is what the tooltip shows instead of a raw day count: a 3-day quarter-day
 * (2h/day) time-off totals 0.75 day, not "3 days".
 */
export function getTimeOffTotalDays(days: PlannerTimeoffDayResponse[]): number {
  if (days.length === 0) return 0;

  // Infer the full working day from a non-hourly day: minutes / fraction.
  let fullWorkingDayMinutes = DEFAULT_CAPACITY_MINUTES;
  for (const day of days) {
    if (day.portion !== DayPortionDTO.HOURLY) {
      const fraction = getPortionFraction(day.portion);
      if (fraction > 0 && day.minutes > 0) {
        fullWorkingDayMinutes = day.minutes / fraction;
        break;
      }
    }
  }

  return days.reduce((total, day) => {
    if (day.portion === DayPortionDTO.HOURLY) {
      return total + (fullWorkingDayMinutes > 0 ? day.minutes / fullWorkingDayMinutes : 0);
    }
    return total + getPortionFraction(day.portion);
  }, 0);
}

/**
 * Formats a fractional day count for display: trims trailing zeros and caps at
 * two decimals (0.75, 1, 2.5). Used by the time-off tooltip.
 */
export function formatTimeOffDays(totalDays: number): number {
  return Math.round(totalDays * 100) / 100;
}

/** A single time-off's contribution on one calendar date. */
export interface TimeOffDayEntry {
  timeOffId: number;
  day: PlannerTimeoffDayResponse;
}

/** Resolved vertical placement for one time-off on one date. */
export interface TimeOffDayRect {
  topPx: number;
  heightPx: number;
}

function isHourly(portion: DayPortionDTO): boolean {
  return portion === DayPortionDTO.HOURLY;
}

/**
 * Computes non-overlapping vertical rects for every time-off present on a
 * single calendar date.
 *
 * Layout rules (matching the time-tracking reference):
 * - HOURLY entries are sized by capacity (`minutes / capacity` of the content
 *   area) and stacked pinned to the BOTTOM of the content area.
 * - Non-HOURLY entries (FULL / HALF / QUARTER) occupy the region above the
 *   hourly stack. When they don't collide they keep their natural day-part
 *   placement (top vs bottom half via `isTopAligned`). When ANY two collide
 *   (e.g. two HALF blocks both dayPartNumber=1), the whole non-hourly region
 *   is divided EQUALLY among all non-hourly blocks so nothing overlaps.
 *
 * Returns a map keyed by `timeOffId` -> rect. The same map is reused for every
 * date in a uniform time-off (all days identical), and recomputed per date for
 * non-uniform ones.
 */
export function computeTimeOffDayLayouts(
  entries: TimeOffDayEntry[],
  capacityMinutes: number,
  cellHeight: number
): Map<number, TimeOffDayRect> {
  const result = new Map<number, TimeOffDayRect>();
  if (entries.length === 0) return result;

  // Blocks are sized as a portion of the ACTUAL content area, so they always
  // fill their share of the current row height and recompute proportionally when
  // the row grows (more assignment levels) or shrinks.
  const contentHeight = cellHeight - HEADER_OFFSET_PX;
  const contentTop = HEADER_OFFSET_PX;
  const contentBottom = contentTop + contentHeight;
  const capacity = capacityMinutes > 0 ? capacityMinutes : DEFAULT_CAPACITY_MINUTES;

  const hourly = entries
    .filter(e => isHourly(e.day.portion))
    .sort((a, b) => a.timeOffId - b.timeOffId);
  const nonHourly = entries.filter(e => !isHourly(e.day.portion));

  // --- HOURLY: capacity-proportional, stacked from the bottom up ---
  // The first hourly block sits flush with the bottom; each subsequent one
  // stacks directly above the previous. Clamp so the stack never exceeds the
  // content area.
  let hourlyStackHeight = 0;
  let cursorBottom = contentBottom;
  for (const entry of hourly) {
    const rawHeight = contentHeight * (entry.day.minutes / capacity);
    const available = cursorBottom - contentTop;
    const heightPx = Math.max(0, Math.min(rawHeight, available));
    const topPx = cursorBottom - heightPx;
    result.set(entry.timeOffId, { topPx, heightPx });
    cursorBottom = topPx;
    hourlyStackHeight += heightPx;
  }

  // --- NON-HOURLY: occupy the region above the hourly stack ---
  const nonHourlyRegionHeight = Math.max(0, contentHeight - hourlyStackHeight);
  const nonHourlyRegionTop = contentTop;

  if (nonHourly.length === 0) return result;

  // Place each non-hourly block in its NATURAL DAY-PART SLOT — a fixed band of
  // the content area determined by portion + dayPartNumber:
  //   QUARTER part N (1-4) → band [ (N-1)·25% , N·25% ]  (height 25%)
  //   HALF    part N (1-2) → band [ (N-1)·50% , N·50% ]  (height 50%)
  //   FULL                 → whole content                (height 100%)
  // This keeps each block at its true HEIGHT (a portion of the full content) and
  // natural position, so a 1st/2nd/4th-quarter on the same day each take 25% and
  // leave a 25% gap. When a block's slot would extend past the hourly stack at the
  // bottom, the block is SHIFTED UP (keeping its natural height) to sit just above
  // the hourly stack — it is never stretched. Only when two non-hourly blocks
  // claim OVERLAPPING slots (true collision) do we fall back to an equal split.
  if (!hasNonHourlySlotCollision(nonHourly)) {
    for (const { timeOffId, day } of nonHourly) {
      const slot = getDayPartSlot(day.portion, day.dayPartNumber);
      const heightPx = contentHeight * slot.fraction;
      const naturalTop = nonHourlyRegionTop + contentHeight * slot.offsetFraction;
      // Clamp into the region above the hourly stack, preserving the natural
      // height (shift up if the slot's bottom would overlap the hourly blocks).
      const maxTop = nonHourlyRegionTop + Math.max(0, nonHourlyRegionHeight - heightPx);
      const topPx = Math.min(naturalTop, maxTop);
      result.set(timeOffId, { topPx, heightPx });
    }
    return result;
  }

  // Overlapping slots — divide the available region equally so all blocks tile
  // it with no overlap.
  layoutEqualSplit(nonHourly, nonHourlyRegionTop, nonHourlyRegionHeight, result);
  return result;
}

/**
 * True when two non-hourly blocks claim overlapping day-part slots (so they
 * can't both sit at their natural band). E.g. two 1st-quarter blocks, or a HALF
 * first-half overlapping a 1st/2nd-quarter. Non-overlapping slots (1st + 2nd +
 * 4th quarter) are NOT a collision and keep their natural placement.
 */
function hasNonHourlySlotCollision(entries: TimeOffDayEntry[]): boolean {
  const ranges = entries.map(({ day }) => {
    const slot = getDayPartSlot(day.portion, day.dayPartNumber);
    return { start: slot.offsetFraction, end: slot.offsetFraction + slot.fraction };
  });
  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      // Overlap with a small epsilon so touching bands (end === start) don't count.
      if (ranges[i].start < ranges[j].end - 0.001 && ranges[j].start < ranges[i].end - 0.001) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Divides the non-hourly region into N equal horizontal slices, ordered by
 * (dayPartNumber asc, timeOffId asc) so part-1 blocks sit above part-2 blocks.
 * Guarantees zero overlap and exact tiling of the region.
 */
function layoutEqualSplit(
  entries: TimeOffDayEntry[],
  regionTop: number,
  regionHeight: number,
  result: Map<number, TimeOffDayRect>
): void {
  const ordered = [...entries].sort((a, b) => {
    const partA = a.day.dayPartNumber ?? 0;
    const partB = b.day.dayPartNumber ?? 0;
    if (partA !== partB) return partA - partB;
    return a.timeOffId - b.timeOffId;
  });
  const sliceHeight = regionHeight / ordered.length;
  ordered.forEach((entry, index) => {
    result.set(entry.timeOffId, {
      topPx: regionTop + index * sliceHeight,
      heightPx: sliceHeight,
    });
  });
}

/** A renderable time-off segment: a contiguous span with a resolved rect. */
export interface TimeOffSegment {
  /** Unique key for React. */
  key: string;
  timeOff: TimeOffBlockType;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  rect: TimeOffDayRect;
  /** Minutes for this segment's day(s) — drives the tooltip's per-day hours. */
  dayMinutes: number;
  /** Day portion (FULL/HALF/QUARTER/HOURLY) for this segment's day(s) — drives
   *  the tooltip's day-part label (e.g. "First quarter"). */
  portion: DayPortionDTO;
  /** Which part of the day (1-based) this segment covers — null for FULL/HOURLY. */
  dayPartNumber: number | null;
  /** True for a uniform whole-block segment (one segment for all days). */
  isWholeBlock: boolean;
}

/**
 * Builds renderable segments for every time-off on a row, resolving each
 * day's vertical rect against all other time-offs sharing that day so nothing
 * overlaps.
 *
 * - Uniform time-offs → a single segment spanning all days.
 * - Non-uniform time-offs → one segment per day.
 *
 * @param timeOffs - All time-offs for the row.
 * @param getCapacityMinutes - Resolver for a date's capacity (minutes/day).
 * @param cellHeight - Row height in px.
 */
export function buildTimeOffSegments(
  timeOffs: TimeOffBlockType[],
  getCapacityMinutes: (dateKey: string) => number,
  cellHeight: number
): TimeOffSegment[] {
  if (timeOffs.length === 0) return [];

  // Group every (timeOff, day) entry by calendar date so a date's layout
  // accounts for all time-offs present on it.
  const entriesByDate = new Map<string, TimeOffDayEntry[]>();
  for (const timeOff of timeOffs) {
    for (const day of timeOff.originalTimeOff.days) {
      const dateKey = dayjs(day.date).format(DATE_KEY_FORMAT);
      const list = entriesByDate.get(dateKey) ?? [];
      list.push({ timeOffId: timeOff.id, day });
      entriesByDate.set(dateKey, list);
    }
  }

  // Resolve each date's layout once.
  const layoutsByDate = new Map<string, Map<number, TimeOffDayRect>>();
  for (const [dateKey, entries] of entriesByDate.entries()) {
    layoutsByDate.set(
      dateKey,
      computeTimeOffDayLayouts(entries, getCapacityMinutes(dateKey), cellHeight)
    );
  }

  const segments: TimeOffSegment[] = [];
  const RECT_EPSILON = 0.5; // px — treat near-identical rects as the same run

  for (const timeOff of timeOffs) {
    const days = [...timeOff.originalTimeOff.days].sort((a, b) => a.date.localeCompare(b.date));
    if (days.length === 0) continue;

    // A uniform time-off (same portion/minutes/dayPart on every day) is drawn
    // as a single bar — UNLESS its neighbors differ across the span and make
    // its resolved rect vary per day (e.g. an HOURLY block appears mid-span and
    // shrinks the available region). In that case we coalesce consecutive days
    // that share the same rect into runs, so each run is one bar. Non-uniform
    // time-offs always split per day. Both paths reduce to "merge consecutive
    // days with the same resolved rect", which the user requested for uniform
    // blocks (one bar) and matches the time-tracking per-day reference for the
    // varying case.
    let runStart: PlannerTimeoffDayResponse | null = null;
    let runEnd: PlannerTimeoffDayResponse | null = null;
    let runRect: TimeOffDayRect | null = null;
    let runIndex = 0;

    const flushRun = () => {
      if (!runStart || !runEnd || !runRect) return;
      const startKey = dayjs(runStart.date).format(DATE_KEY_FORMAT);
      segments.push({
        key: `timeoff-${timeOff.id}-${startKey}-${runIndex++}`,
        timeOff,
        startDate: dayjs(runStart.date),
        endDate: dayjs(runEnd.date),
        rect: runRect,
        dayMinutes: runStart.minutes,
        portion: runStart.portion,
        dayPartNumber: runStart.dayPartNumber,
        isWholeBlock: runStart === days[0] && runEnd === days[days.length - 1],
      });
      runStart = null;
      runEnd = null;
      runRect = null;
    };

    for (const day of days) {
      const dateKey = dayjs(day.date).format(DATE_KEY_FORMAT);
      const rect = layoutsByDate.get(dateKey)?.get(timeOff.id);
      if (!rect) {
        flushRun();
        continue;
      }

      const sameAsRun =
        runRect !== null &&
        runStart !== null &&
        runEnd !== null &&
        Math.abs(runRect.topPx - rect.topPx) < RECT_EPSILON &&
        Math.abs(runRect.heightPx - rect.heightPx) < RECT_EPSILON &&
        // Days must be consecutive to merge into one bar.
        dayjs(day.date).diff(dayjs(runEnd.date), 'day') === 1 &&
        // Per-day hours must match so the tooltip's hours/day stays accurate.
        runStart.minutes === day.minutes;

      if (sameAsRun) {
        runEnd = day;
      } else {
        flushRun();
        runStart = day;
        runEnd = day;
        runRect = rect;
      }
    }
    flushRun();
  }

  return segments;
}
